import { PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import isEmpty from 'lodash/isEmpty';
import { MemberType, Role, Team } from 'api/models';
import { ResourceType } from 'api/resource-management/types';
import { TemplateObjectType, TemplateState } from 'data/templates/api';
// eslint-disable-next-line import/no-internal-modules
import { getPlaceholdersFromTemplate } from 'routes/projects/create/project-info-step-utils';
import { floatUid } from 'utils/uid';
import { normalizeTemplatePayload } from '../api/transform';
import {
  ProjectTemplate,
  ProjectTemplateApi,
  ProjectTemplateProjectPhase,
  ProjectTemplateStore,
  ProjectTemplateTask,
  TemplateMeta,
} from '../types';

const trimDeletedFormTasks = (projectPhases: ProjectTemplateProjectPhase[]) => {
  const removedTaskList = [] as number[];
  const updatedProjectPhases = projectPhases.map((phase) => {
    return {
      ...phase,
      tasks: phase.tasks.reduce(
        (acc: ProjectTemplateTask[], task: ProjectTemplateTask) => {
          if (task.formTemplateId && task.formTemplateDeleted) {
            removedTaskList.push(task.taskId);

            return acc;
          }

          acc.push(task);

          return acc;
        },
        [],
      ),
    };
  });

  if (!removedTaskList.length) {
    return updatedProjectPhases;
  }

  // remove the deleted form task from the dependentTask as well
  return updatedProjectPhases.map((phase) => {
    return {
      ...phase,
      tasks: phase.tasks.map((task) => {
        return {
          ...task,
          dependentTasks: (task.dependentTasks || []).filter(
            (depTask) => !removedTaskList.includes(depTask.taskId),
          ),
        };
      }),
    };
  });
};
const removeInvalidPlaceholdersFromTasks = (
  projectPhases: ProjectTemplateProjectPhase[],
  placeholderLookup: { [id: number]: Team } = {},
) => {
  const updateTask = (task: ProjectTemplateTask) => ({
    ...task,
    assignee: {
      ...task?.assignee,
      users: task?.assignee?.users ?? [],
      teams: (task?.assignee?.teams ?? []).filter(
        (team) =>
          team.roleId === -1 ||
          team.teamType === MemberType.External ||
          placeholderLookup[team.teamId] != null,
      ),
    },
    subTasks: (task?.subTasks ?? []).map(updateTask),
  });

  return projectPhases.map((projectPhase) => ({
    ...projectPhase,
    tasks: (projectPhase?.tasks ?? []).map(updateTask),
  }));
};

export const templateSlice = {
  fetchProjectTemplateStart: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<AtLeast<ProjectTemplate, 'templateId'>>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${payload.templateId}.loading`,
      true,
    );
  },

  changeTemplateSaveState: (
    state: ProjectTemplateStore,
    {
      payload,
    }: PayloadAction<{
      state: boolean;
      template: AtLeast<ProjectTemplate, 'templateId'>;
    }>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${payload.template.templateId}.isSaving`,
      payload.state,
    );
  },

  fetchProjectTemplateStartSuccess: (
    state: ProjectTemplateStore,
    {
      payload,
    }: PayloadAction<{
      template: ProjectTemplateApi;
      allRoles?: Role[];
      rolesWithPlaceholder?: Role[];
      teamsLookup: { [id: number]: Team };
    }>,
  ): ProjectTemplateStore => {
    const { template, rolesWithPlaceholder, allRoles, teamsLookup } = payload;
    const {
      templateContent: { projectPhases },
    } = template;

    const sortedProjectPhases = [...projectPhases].sort(
      (projectPhaseA, projectPhasesB) =>
        projectPhaseA.startDay - projectPhasesB.startDay,
    );

    const normalizeProjectPhases = trimDeletedFormTasks(sortedProjectPhases);

    const placeholders = getPlaceholdersFromTemplate({
      template,
      rolesWithPlaceholder,
      teamsLookup,
    });
    const payloadWithSortedProjectPhases = {
      ...template,
      templateContent: {
        ...template.templateContent,
        projectPhases: removeInvalidPlaceholdersFromTasks(
          normalizeProjectPhases,
          placeholders,
        ),
      },
    } as ProjectTemplateApi;

    const { allocations, allocatedMembers } =
      payloadWithSortedProjectPhases.templateContent;

    const updatedAllocatedMembers = {
      users: allocatedMembers?.users ?? [],
      teams: (allocatedMembers?.teams ?? []).filter(
        (team) => !!allRoles?.find((x) => x.roleId === team.roleId),
      ),
    };
    const allocationMap = (allocations ?? []).reduce((result, x) => {
      const allocation = {
        ...x,
        type: (x as any).userId != null ? ResourceType.User : ResourceType.Team,
      } as typeof x;

      if (
        allocation.type === ResourceType.Team &&
        !updatedAllocatedMembers.teams.find(
          (team) => allocation.teamId === team.teamId,
        )
      ) {
        return result;
      }

      result[x.id] = allocation;

      return result;
    }, {});

    const { entities } = normalizeTemplatePayload(
      payloadWithSortedProjectPhases,
    );

    const result = dotPropImmutable.merge(
      state,
      `data.${payloadWithSortedProjectPhases.templateId}`,
      {
        template: entities.template[payloadWithSortedProjectPhases.templateId],
        projectPhases: entities.projectPhases || {},
        tasks: entities.tasks || {},
        spaceList: entities.spaceList || {},
        placeholders,
        loading: false,
        allocations: allocationMap,
        allocatedMembers: updatedAllocatedMembers,
        spaceSetup: isEmpty(
          payloadWithSortedProjectPhases.templateContent.spaceList,
        )
          ? 'YET_TO_START'
          : 'COMPLETE',
      },
    );

    return {
      ...result,
      activeId: payloadWithSortedProjectPhases.templateId,
    };
  },

  createProjectTemplate: (
    state: ProjectTemplateStore,
    {
      payload: { templateName, templateContent, isFromIdb, templateMeta },
    }: PayloadAction<{
      templateName: string;
      templateContent?: ProjectTemplateApi['templateContent'];
      isFromIdb?: boolean;
      templateMeta?: TemplateMeta;
    }>,
  ): ProjectTemplateStore => {
    const templateId = 'new';
    const emptyPhase: ProjectTemplateProjectPhase = {
      projectPhaseId: floatUid(),
      projectPhaseName: '',
      isNew: true,
      private: false,
      startDay: 1,
      duration: 1,
      tasks: [],
    };

    const emptyTemplateContent = {
      duration: 0,
      projectPhases: [emptyPhase],
      private: false,
      startDay: 0,
      spaceList: [],
    };

    const { entities } = normalizeTemplatePayload({
      templateId,
      templateContent: templateContent ?? emptyTemplateContent,
      templateName,
      objectType: TemplateObjectType.Project,
      state: TemplateState.Published,
      templateMeta,
    });

    const result = dotPropImmutable.merge(state, `data.${templateId}`, {
      error: null,
      template: entities.template[templateId],
      projectPhases: entities.projectPhases || {},
      tasks: entities.tasks || {},
      spaceList: entities.spaceList || {},
      loading: false,
      spaceSetup:
        isFromIdb && isEmpty(templateContent?.spaceList)
          ? 'COMPLETE'
          : 'YET_TO_START',
      dynamicConditions: {},
      placeholders: {},
      allocations: templateContent?.allocations ?? {},
      allocatedMembers: templateContent?.allocatedMembers ?? {
        users: [],
        teams: [],
      },
    });

    return {
      ...result,
      activeId: templateId,
      activeRecord: undefined,
    };
  },

  toggleProjectToTemplate: (
    state: ProjectTemplateStore,
    {
      payload: { isProjectToTemplate },
    }: PayloadAction<{ isProjectToTemplate: boolean }>,
  ): ProjectTemplateStore => {
    return {
      ...state,
      projectToTemplate: isProjectToTemplate,
    };
  },

  updateProjectTemplate: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<ProjectTemplate>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${payload.templateId}.template`,
      payload,
    );
  },

  updateSpacesList: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<any>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${payload.templateId}.spaceList`,
      payload.spaceList,
    );
  },

  updateSpaceListOrder: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<any>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${payload.templateId}.template.templateContent.spaceList`,
      payload.spaceListOrder,
    );
  },

  updateSpaceOrder: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<any>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${payload.templateId}.template.templateContent.spaceOrder`,
      payload.spaceOrder,
    );
  },

  clearProjectTemplate: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<ProjectTemplate>,
  ): ProjectTemplateStore => {
    return {
      ...state,
      activeId: 'new',
      ...dotPropImmutable.delete(state, `data.${payload.templateId}`),
    };
  },

  saveTemplatePlaceholder: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<Team>,
  ): ProjectTemplateStore => {
    const templateData = state.data[state.activeId];

    return {
      ...state,
      data: {
        ...state.data,
        [state.activeId]: {
          ...templateData,
          placeholders: {
            ...templateData.placeholders,
            [payload.teamId]: payload,
          },
        },
      },
    };
  },
};
